// /admin/integrations — SCM integration config + Jira outbound config (T123, T195a, T268).
//
// Layout:
//   - Header with eyebrow and page title.
//   - One card per SCM provider (GitHub App, GitLab, Azure DevOps, Bitbucket).
//   - Each card shows:
//       · provider logo / name + connection status pill
//       · Inbound webhook URL with a copy-to-clipboard button
//       · Event subscriptions: which events to configure on the SCM side
//       · "Test connection" button wired to POST …/health-check
//       · For GitHub when unconfigured: "Set up GitHub App" link
//   - Jira section: full CRUD config form (T195a).

import { useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR, { mutate as globalMutate } from 'swr';
import {
  AlertCircle,
  CheckCircle2,
  Clipboard,
  ClipboardCheck,
  ExternalLink,
  GitBranch,
  Loader2,
  Plug,
  Settings,
  XCircle,
} from 'lucide-react';
import {
  ADMIN_INTEGRATIONS_KEY,
  ADMIN_JIRA_KEY,
  AdminIntegrationsResponse,
  IntegrationHealthCheck,
  IntegrationProvider,
  JiraConfig,
  adminIntegrationsApi,
  jiraApi,
  ApiError,
  fetcher,
} from '../lib/api';
import { PageHeader } from '../components/ui';
import { safeHref } from '../lib/safeHref'

// ---------------------------------------------------------------------------
// Per-provider metadata
// ---------------------------------------------------------------------------

interface ProviderMeta {
  label: string;
  description: string;
  /** Path to the inbound webhook endpoint on this server. */
  webhookPath: string | null;
  /** Events to subscribe to on the SCM side. */
  events: string[];
  /** Step-by-step instructions to configure the webhook in the SCM UI. */
  setupInstructions: string;
  /** Env var hint for credential setup. */
  envHint: string;
}

const PROVIDER_META: Record<string, ProviderMeta> = {
  github: {
    label: 'GitHub App',
    description:
      'PR check runs, inline annotations, and installation webhooks via the GitHub App.',
    webhookPath: '/github/app/webhooks',
    events: ['pull_request', 'push', 'installation', 'check_run'],
    setupInstructions:
      'Webhook delivery is handled automatically by the GitHub App during installation.',
    envHint: 'ORBITALLINT_GITHUB_APP_ID + ORBITALLINT_GITHUB_APP_PRIVATE_KEY',
  },
  gitlab: {
    label: 'GitLab',
    description: 'MR discussions and pipeline status reports via GitLab webhooks.',
    webhookPath: '/api/v1/webhooks/scm/gitlab',
    events: ['Push events', 'Merge request events', 'Comments'],
    setupInstructions:
      'In GitLab → Settings → Webhooks, add the URL above and select the events below.',
    envHint: 'ORBITALLINT_GITLAB_TOKEN',
  },
  azure: {
    label: 'Azure DevOps',
    description: 'PR status updates and comment threads via Azure Service Hooks.',
    webhookPath: '/api/v1/webhooks/scm/azure',
    events: ['Pull request created', 'Pull request updated', 'Code pushed'],
    setupInstructions:
      'In Azure DevOps → Project Settings → Service Hooks, create a "Web Hooks" subscription pointing to the URL above.',
    envHint: 'ORBITALLINT_AZUREDEVOPS_TOKEN',
  },
  bitbucket: {
    label: 'Bitbucket Cloud',
    description: 'PR decoration and build-status via Bitbucket webhooks.',
    webhookPath: '/api/v1/webhooks/scm/bitbucket',
    events: ['Repository: Push', 'Pull Request: Created', 'Pull Request: Updated'],
    setupInstructions:
      'In Bitbucket → Repository Settings → Webhooks, add the URL above and select the events below.',
    envHint: 'ORBITALLINT_BITBUCKET_TOKEN (or USERNAME + APP_PASSWORD)',
  },
};

// ---------------------------------------------------------------------------
// Page root
// ---------------------------------------------------------------------------

export function AdminIntegrations() {
  const { data, error, isLoading } =
    useSWR<AdminIntegrationsResponse>(ADMIN_INTEGRATIONS_KEY, fetcher, {
      revalidateOnFocus: false,
    });

  let providersContent: React.ReactNode;
  if (isLoading) {
    providersContent = (
      <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-sm">Loading integration status…</span>
      </div>
    );
  } else if (error) {
    providersContent = <ErrorBanner error={error} />;
  } else if (data) {
    providersContent = (
      <div className="space-y-4">
        {data.providers.map((p) => (
          <ProviderCard key={p.provider} provider={p} />
        ))}
      </div>
    );
  } else {
    providersContent = null;
  }

  return (
    <div className="max-w-3xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<Plug />}
        title="Integrations"
        description={
          <>
            Connect orbital-lint to your SCM providers. Configure each provider's
            credentials via server environment variables, then copy the inbound
            webhook URL into your SCM settings.
          </>
        }
        actions={
          <Link
            to="/admin/settings"
            className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg transition-colors"
          >
            <Settings className="h-3.5 w-3.5" aria-hidden="true" />
            Settings
          </Link>
        }
      />

      <h2 className="font-semibold text-sm text-fg-muted uppercase tracking-wide mb-3">
        Source Control
      </h2>

      {providersContent}

      {/* T195a: Jira outbound integration */}
      <div className="mt-8">
        <h2 className="font-semibold text-sm text-fg-muted uppercase tracking-wide mb-3">
          Issue Tracking
        </h2>
        <JiraSection />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Provider card (expanded config form)
// ---------------------------------------------------------------------------

interface ProviderCardProps {
  readonly provider: IntegrationProvider;
}

function ProviderCard({ provider }: ProviderCardProps) {
  const meta: ProviderMeta = PROVIDER_META[provider.provider] ?? {
    label: provider.provider,
    description: '',
    webhookPath: null,
    events: [],
    setupInstructions: '',
    envHint: '',
  };

  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<IntegrationHealthCheck | null>(null);
  const [checkErr, setCheckErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const webhookUrl = meta.webhookPath
    ? `${globalThis.location.origin}${meta.webhookPath}`
    : null;

  const handleCopy = () => {
    if (!webhookUrl) return;
    navigator.clipboard.writeText(webhookUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleCheck = async () => {
    setChecking(true);
    setCheckResult(null);
    setCheckErr(null);
    try {
      const res = await adminIntegrationsApi.healthCheck(provider.provider);
      setCheckResult(res);
    } catch (e) {
      let checkErrMsg: string;
      if (e instanceof ApiError) {
        checkErrMsg = `${e.code}: ${e.message}`;
      } else if (e instanceof Error) {
        checkErrMsg = e.message;
      } else {
        checkErrMsg = 'check failed';
      }
      setCheckErr(checkErrMsg);
    } finally {
      setChecking(false);
    }
  };

  return (
    <section className="orb-card p-5">
      {/* Header row */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <ProviderIcon provider={provider.provider} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-semibold text-sm text-fg">{meta.label}</h2>
              <StatusPill configured={provider.configured} />
            </div>
            <p className="text-[11px] text-fg-muted mt-0.5 leading-relaxed">
              {meta.description}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {!provider.configured && provider.setup_url && (
            <a
              href={safeHref(provider.setup_url)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-[0.08em] bg-accent text-accent-fg hover:opacity-90 border border-accent/40 transition-opacity"
            >
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
              Set up
            </a>
          )}
          <button
            type="button"
            onClick={handleCheck}
            disabled={checking}
            className={checkBtnCls}
          >
            {checking ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                Checking…
              </>
            ) : (
              'Test connection'
            )}
          </button>
        </div>
      </div>

      {/* Inbound webhook URL */}
      {webhookUrl && (
        <div className="mb-4">
          <p className="text-xs text-fg-muted mb-1.5 font-medium">
            Inbound webhook URL
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate text-xs font-mono bg-bg-inset text-fg px-3 py-2 rounded-md border border-border">
              {webhookUrl}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              title="Copy webhook URL"
              className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-2 rounded-md text-xs border transition-colors ${
                copied
                  ? 'border-success/40 text-success bg-success/10'
                  : 'border-border text-fg-muted hover:text-fg hover:border-fg-muted'
              }`}
            >
              {copied ? (
                <ClipboardCheck className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <Clipboard className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              <span>{copied ? 'Copied' : 'Copy'}</span>
            </button>
          </div>
          <p className="text-[11px] text-fg-subtle mt-1.5">{meta.setupInstructions}</p>
        </div>
      )}

      {/* Event subscriptions */}
      {meta.events.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-fg-muted mb-1.5 font-medium">
            Recommended event subscriptions
          </p>
          <div className="flex flex-wrap gap-1.5">
            {meta.events.map((ev) => (
              <span
                key={ev}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-bg-inset border border-border text-fg-muted"
              >
                {ev}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Credentials hint */}
      <div className="border-t border-border pt-3">
        <p className="text-[11px] text-fg-subtle">
          <span className="font-medium text-fg-muted">Credentials:</span>{' '}
          Set{' '}
          <code className="font-mono bg-bg-inset px-1 py-0.5 rounded text-accent text-[10px]">
            {meta.envHint}
          </code>{' '}
          on the server to enable API access and outbound decoration.
        </p>
      </div>

      {/* Health check result */}
      {checkResult && (
        <div
          className={`mt-3 flex items-start gap-2 text-xs rounded-md px-3 py-2 ${
            checkResult.healthy
              ? 'bg-success/10 border border-success/30 text-success'
              : 'bg-danger/10 border border-danger/30 text-danger'
          }`}
        >
          {checkResult.healthy ? (
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
          ) : (
            <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
          )}
          <span>
            {checkResult.healthy
              ? `Connection healthy — ${checkResult.latency_ms} ms`
              : `Connection failed — ${checkResult.error || 'unknown error'}`}
          </span>
        </div>
      )}
      {checkErr && (
        <div className="mt-3 flex items-start gap-2 text-xs rounded-md px-3 py-2 bg-danger/10 border border-danger/30 text-danger">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
          <span>{checkErr}</span>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Jira section (T195a)
// ---------------------------------------------------------------------------

function extractErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return `${e.code}: ${e.message}`;
  if (e instanceof Error) return e.message;
  return fallback;
}

function JiraSection() {
  const { data: cfg, isLoading, error } = useSWR<JiraConfig | null>(
    ADMIN_JIRA_KEY,
    fetcher,
    { revalidateOnFocus: false },
  );

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const [form, setForm] = useState({
    base_url: '',
    project_key: '',
    default_issue_type: 'Bug',
    api_email: '',
    api_token: '',
  });

  const startEdit = () => {
    setForm({
      base_url: cfg?.base_url ?? '',
      project_key: cfg?.project_key ?? '',
      default_issue_type: cfg?.default_issue_type ?? 'Bug',
      api_email: cfg?.api_email ?? '',
      api_token: '',
    });
    setTestResult(null);
    setSaveErr(null);
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveErr(null);
    try {
      await jiraApi.saveConfig({
        base_url: form.base_url,
        project_key: form.project_key,
        default_issue_type: form.default_issue_type,
        api_email: form.api_email,
        api_token: form.api_token || undefined,
      });
      await globalMutate(ADMIN_JIRA_KEY);
      setEditing(false);
    } catch (e) {
      setSaveErr(extractErrorMessage(e, 'save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await jiraApi.testConnection({
        base_url: form.base_url || undefined,
        api_email: form.api_email || undefined,
        api_token: form.api_token || undefined,
      });
      setTestResult(res);
    } catch (e) {
      const testErrMsg = e instanceof ApiError ? e.message : extractErrorMessage(e, 'test failed');
      setTestResult({ ok: false, error: testErrMsg });
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Remove Jira configuration?')) return;
    await jiraApi.deleteConfig();
    await globalMutate(ADMIN_JIRA_KEY);
  };

  if (isLoading) {
    return (
      <div className="orb-card p-5 flex items-center gap-2 text-fg-muted text-sm">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading…
      </div>
    );
  }

  if (error && !(error instanceof ApiError && error.status === 402)) {
    return (
      <div className="orb-card p-5 text-sm text-danger border-danger/30">
        Failed to load Jira config: {error instanceof Error ? error.message : 'error'}
      </div>
    );
  }

  if (error instanceof ApiError && error.status === 402) {
    return (
      <div className="orb-card p-5 text-sm text-fg-muted">
        Jira integration requires a Trial or Enterprise license.
        <Link to="/admin/license" className="ml-2 text-accent hover:underline">
          Manage license →
        </Link>
      </div>
    );
  }

  return (
    <section className="orb-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-bg-inset flex items-center justify-center text-xs font-bold text-accent" aria-hidden="true">J</div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm text-fg">Jira</h3>
              {cfg ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-success/15 text-success">
                  <CheckCircle2 className="h-2.5 w-2.5" aria-hidden="true" />
                  Connected
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-fg-subtle/20 text-fg-muted">
                  <XCircle className="h-2.5 w-2.5" aria-hidden="true" />
                  Not configured
                </span>
              )}
            </div>
            <p className="text-[11px] text-fg-muted mt-0.5">
              Create Jira tickets from orbital findings. Jira Cloud only (v1).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {cfg && !editing && (
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-danger/40 text-danger hover:bg-danger/10 transition-colors"
            >
              Remove
            </button>
          )}
          {!editing && (
            <button
              type="button"
              onClick={startEdit}
              className={checkBtnCls}
            >
              {cfg ? 'Edit' : 'Configure'}
            </button>
          )}
        </div>
      </div>

      {cfg && !editing && (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mt-2">
          <dt className="text-fg-muted">Instance</dt>
          <dd className="text-fg font-mono truncate">{cfg.base_url}</dd>
          <dt className="text-fg-muted">Project</dt>
          <dd className="text-fg">{cfg.project_key}</dd>
          <dt className="text-fg-muted">Issue type</dt>
          <dd className="text-fg">{cfg.default_issue_type}</dd>
          <dt className="text-fg-muted">API email</dt>
          <dd className="text-fg">{cfg.api_email}</dd>
          <dt className="text-fg-muted">API token</dt>
          <dd className="text-fg font-mono">{cfg.api_token_masked}</dd>
        </dl>
      )}

      {editing && (
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">Instance URL</span>
              <input
                className="orb-input text-xs"
                placeholder="https://yourorg.atlassian.net"
                value={form.base_url}
                onChange={(e) => setForm({ ...form, base_url: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">Project key</span>
              <input
                className="orb-input text-xs"
                placeholder="PROJ"
                value={form.project_key}
                onChange={(e) => setForm({ ...form, project_key: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">Default issue type</span>
              <input
                className="orb-input text-xs"
                placeholder="Bug"
                value={form.default_issue_type}
                onChange={(e) => setForm({ ...form, default_issue_type: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-fg-muted">API email</span>
              <input
                className="orb-input text-xs"
                placeholder="you@example.com"
                value={form.api_email}
                onChange={(e) => setForm({ ...form, api_email: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 col-span-2">
              <span className="text-xs text-fg-muted">
                API token {cfg ? '(leave blank to keep existing)' : ''}
              </span>
              <input
                className="orb-input text-xs font-mono"
                type="password"
                placeholder="Atlassian API token"
                value={form.api_token}
                onChange={(e) => setForm({ ...form, api_token: e.target.value })}
              />
            </label>
          </div>

          {testResult && (
            <div className={`flex items-start gap-2 text-xs rounded-md px-3 py-2 ${testResult.ok ? 'bg-success/10 border border-success/30 text-success' : 'bg-danger/10 border border-danger/30 text-danger'}`}>
              {testResult.ok
                ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
                : <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />}
              <span>{testResult.ok ? 'Connection successful!' : `Connection failed: ${testResult.error}`}</span>
            </div>
          )}
          {saveErr && (
            <div className="flex items-start gap-2 text-xs rounded-md px-3 py-2 bg-danger/10 border border-danger/30 text-danger">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" aria-hidden="true" />
              <span>{saveErr}</span>
            </div>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleTest}
              disabled={testing}
              className={checkBtnCls + ' disabled:opacity-40 disabled:cursor-not-allowed'}
            >
              {testing ? <><Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> Testing…</> : 'Test connection'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-[0.08em] bg-accent text-accent-fg hover:opacity-90 border border-accent/40 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <><Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> Saving…</> : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-xs text-fg-muted hover:text-fg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatusPill({ configured }: { readonly configured: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
        configured
          ? 'bg-success/15 text-success'
          : 'bg-fg-subtle/20 text-fg-muted'
      }`}
    >
      {configured ? (
        <CheckCircle2 className="h-2.5 w-2.5" aria-hidden="true" />
      ) : (
        <XCircle className="h-2.5 w-2.5" aria-hidden="true" />
      )}
      {configured ? 'Connected' : 'Not configured'}
    </span>
  );
}

function ProviderIcon({ provider }: { readonly provider: string }) {
  const cls = 'h-8 w-8 rounded-lg bg-bg-inset flex items-center justify-center text-fg-muted shrink-0';
  return (
    <div className={cls} title={provider} aria-label={provider} role="img">
      <GitBranch className="h-4 w-4" aria-hidden="true" />
    </div>
  );
}

function ErrorBanner({ error }: { readonly error: unknown }) {
  let msg: string;
  if (error instanceof ApiError) {
    msg = `${error.code}: ${error.message}`;
  } else if (error instanceof Error) {
    msg = error.message;
  } else {
    msg = 'request failed';
  }
  const hint =
    error instanceof ApiError && error.code === 'insufficient_role'
      ? 'Only org admins can view integration status.'
      : null;
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">{msg}</p>
      {hint ? <p className="text-xs text-fg-muted mt-2">{hint}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const checkBtnCls = `
  inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  border border-border text-fg-muted hover:text-fg hover:border-fg-muted
  transition-colors disabled:opacity-40 disabled:cursor-not-allowed
`.trim();
